import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { categories } from "@/mocks/categories";

function getLangFromPath() {
  const path = window.location.pathname;
  if (path.startsWith("/pt")) return "pt";
  if (path.startsWith("/de")) return "de";
  return "en";
}

function getBasePath() {
  const path = window.location.pathname;
  if (path.startsWith("/pt")) return "/pt";
  if (path.startsWith("/de")) return "/de";
  return "";
}

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatsOpen, setMobileCatsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const currentLang = getLangFromPath();
  const basePath = getBasePath();

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 40);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangMenuOpen(false);
      }
    }
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  function switchLanguage(lang: string) {
    const path = window.location.pathname;
    let newPath = path;
    if (path.startsWith("/pt") || path.startsWith("/de") || path.startsWith("/en")) {
      newPath = path.replace(/^\/(pt|de|en)/, lang === "en" ? "" : `/${lang}`);
    } else if (lang !== "en") {
      newPath = `/${lang}${path}`;
    }
    i18n.changeLanguage(lang);
    window.location.href = newPath;
  }

  function goHome() {
    const target = basePath || "/";
    window.location.href = target;
  }

  const guideSlug = "cssbuy-spreadsheet-buying-guide";
  const productsPath = `${basePath}/products`;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-shadow duration-300 bg-background-50/95 backdrop-blur-md border-b border-background-200/60 ${scrolled ? "shadow-sm" : ""}`}>
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          <button
            onClick={goHome}
            className="text-base md:text-lg font-heading font-semibold tracking-tight cursor-pointer whitespace-nowrap text-foreground-950"
          >
            CSSBuy<span className="text-primary-500">BR</span>
          </button>

          <div className="hidden md:flex items-center gap-6">
            <button
              onClick={goHome}
              className="text-sm font-medium cursor-pointer whitespace-nowrap text-foreground-800 hover:text-primary-600 transition-colors"
            >
              {t("nav.home")}
            </button>

            <a
              href={productsPath}
              className="text-sm font-medium whitespace-nowrap text-foreground-800 hover:text-primary-600 transition-colors"
            >
              {t("nav.products")}
            </a>

            <a
              href={`${basePath}/article/${guideSlug}`}
              className="text-sm font-medium whitespace-nowrap text-foreground-800 hover:text-primary-600 transition-colors"
            >
              {t("nav.guide")}
            </a>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 text-sm font-medium cursor-pointer whitespace-nowrap text-foreground-800 hover:text-primary-600 transition-colors"
              >
                {t("nav.categories")}
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-lg shadow-lg border border-background-200/60 py-2 max-h-[340px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2.5 px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                    >
                      <img
                        src={cat.image}
                        alt={cat.name}
                        className="w-8 h-8 rounded object-cover shrink-0"
                      />
                      <span className="whitespace-nowrap">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href={`${basePath}/article/cssbuy-spreadsheet-2026-complete-guide`}
              className="text-sm font-medium whitespace-nowrap text-foreground-800 hover:text-primary-600 transition-colors"
            >
              {t("nav.article")}
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-medium whitespace-nowrap bg-primary-500 text-background-50 px-3.5 py-1.5 rounded-md hover:bg-primary-600 transition-colors"
            >
              {t("nav.spreadsheet")}
            </a>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center gap-1 text-sm font-medium cursor-pointer whitespace-nowrap text-foreground-800"
              >
                <i className="ri-global-line"></i>
                <span className="uppercase">{currentLang}</span>
                <i className="ri-arrow-down-s-line"></i>
              </button>
              {langMenuOpen && (
                <div className="absolute top-full right-0 mt-2 w-36 bg-background-50 rounded-lg shadow-lg border border-background-200/60 py-1 z-50">
                  <button
                    onClick={() => switchLanguage("en")}
                    className="w-full text-left px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    {t("lang.en")}
                  </button>
                  <button
                    onClick={() => switchLanguage("pt")}
                    className="w-full text-left px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    {t("lang.pt")}
                  </button>
                  <button
                    onClick={() => switchLanguage("de")}
                    className="w-full text-left px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    {t("lang.de")}
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden flex items-center justify-center w-8 h-8 cursor-pointer text-foreground-900"
            >
              <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/60 px-4 pb-4 max-h-[70vh] overflow-y-auto">
          <button
            onClick={() => { goHome(); setMobileOpen(false); }}
            className="flex items-center gap-2 w-full text-left py-3 text-sm font-semibold text-foreground-900 border-b border-background-200/40 cursor-pointer"
          >
            <i className="ri-home-4-line text-base"></i>
            {t("nav.home")}
          </button>

          <a
            href={productsPath}
            className="flex items-center gap-2 py-3 text-sm font-semibold text-foreground-900 border-b border-background-200/40"
            onClick={() => setMobileOpen(false)}
          >
            <i className="ri-store-2-line text-base"></i>
            {t("nav.products")}
          </a>

          <a
            href={`${basePath}/article/${guideSlug}`}
            className="flex items-center gap-2 py-3 text-sm font-semibold text-foreground-900 border-b border-background-200/40"
            onClick={() => setMobileOpen(false)}
          >
            <i className="ri-shopping-bag-3-line text-base"></i>
            {t("nav.guide")}
          </a>

          <button
            onClick={() => setMobileCatsOpen(!mobileCatsOpen)}
            className="flex items-center justify-between w-full py-3 text-sm font-semibold text-foreground-900 border-b border-background-200/40 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <i className="ri-layout-grid-line text-base"></i>
              {t("nav.categories")}
            </span>
            <i className={`ri-arrow-down-s-line transition-transform ${mobileCatsOpen ? "rotate-180" : ""}`}></i>
          </button>

          {mobileCatsOpen && (
            <div className="grid grid-cols-2 gap-2 pt-3 pb-2">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 p-2 rounded-md border border-background-200/50 bg-background-50 hover:bg-background-100 transition-colors"
                >
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-9 h-9 rounded object-cover shrink-0"
                  />
                  <span className="text-xs text-foreground-700 leading-tight">{cat.name}</span>
                </a>
              ))}
            </div>
          )}

          <a
            href={`${basePath}/article/cssbuy-spreadsheet-2026-complete-guide`}
            className="flex items-center gap-2 py-3 text-sm font-semibold text-foreground-900 border-b border-background-200/40"
            onClick={() => setMobileOpen(false)}
          >
            <i className="ri-article-line text-base"></i>
            {t("nav.article")}
          </a>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center gap-2 py-3 text-sm font-semibold text-primary-600"
          >
            <i className="ri-external-link-line text-base"></i>
            {t("nav.spreadsheet")}
          </a>
        </div>
      )}
    </nav>
  );
}